import type { MeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import type { DocumentSchemaField } from '@/types/measure-document-types';
import { computed, ref } from 'vue';
import { createMeasureDocumentAdapter } from '@/adapters/measure-document-adapter';

// Module-level: the schema is the shape of the data model, identical for every
// surface and every tenant, so one fetch serves the whole session.
const fields = ref<DocumentSchemaField[]>([]);
const isLoading = ref(false);
let inFlight: Promise<void> | null = null;

/**
 * The document metadata schema — every field a document can be filtered on.
 *
 * Distinct from a result set's populated fields: the schema is what the user may
 * ask for, while `availableFields` on a fetch is what the current results happen
 * to carry (and so drives table columns). Offering only the latter would hide a
 * filter whenever the current page didn't populate it.
 */
export function useDocumentSchema(
   adapter: MeasureDocumentAdapter = createMeasureDocumentAdapter(),
) {
   async function loadSchema(): Promise<void> {
      if (fields.value.length > 0)
         return;
      // Concurrent callers share one request rather than racing; several
      // surfaces mount at once on a page with a filter bar.
      if (inFlight) {
         await inFlight;
         return;
      }

      isLoading.value = true;
      inFlight = adapter
         .schema()
         .then((response) => {
            // An envelope without a `data` array degrades the picker the same
            // way a failed request does, rather than leaving `fields` undefined
            // for every consumer that maps over it.
            fields.value = Array.isArray(response) ? response : [];
         })
         .catch((e) => {
            // An empty field list degrades the filter picker to bureau/doc-type
            // rather than breaking the page.
            console.error('Failed to load the document schema:', e);
         })
         .finally(() => {
            isLoading.value = false;
            inFlight = null;
         });

      await inFlight;
   }

   /** `GROUP.CODE` keys, which is how filter state addresses a field. */
   const schemaFieldKeys = computed(() => fields.value.map(f => `${f.group}.${f.code}`));

   const schemaFieldsByGroup = computed(() => {
      const grouped = new Map<string, DocumentSchemaField[]>();
      for (const field of fields.value) {
         const existing = grouped.get(field.group);
         if (existing)
            existing.push(field);
         else
            grouped.set(field.group, [field]);
      }
      return grouped;
   });

   return {
      schemaFields: fields,
      schemaFieldKeys,
      schemaFieldsByGroup,
      isSchemaLoading: isLoading,
      loadSchema,
   };
}
